/**
 * Utility functions for Reo tracking integration
 */

// Type definition for Reo tracking
interface Reo {
  identify: (params: { username: string; type: 'linkedin' | 'github' | 'email' }) => void;
}

// Type definition for window with Reo
interface WindowWithReo extends Window {
  Reo?: Reo;
}

/**
 * Ensures Reo tracking is available using exponential backoff polling
 * Uses exponential backoff polling to wait for Reo to load via GTM
 *
 * @param maxAttempts Maximum number of polling attempts (default: 10)
 * @returns Promise<Reo> - Resolves with Reo instance
 * @throws Error if Reo is not available after maxAttempts
 */
const ensureReo = async (maxAttempts: number = 10): Promise<Reo> => {
  const windowWithReo = window as WindowWithReo;

  // Check if Reo is already available
  if (windowWithReo.Reo && typeof windowWithReo.Reo.identify === 'function') {
    return windowWithReo.Reo;
  }

  // Poll for Reo with exponential backoff
  let attempts = 0;
  const pollForReo = async (): Promise<Reo> => {
    attempts += 1;

    if (windowWithReo.Reo && typeof windowWithReo.Reo.identify === 'function') {
      return windowWithReo.Reo;
    }

    if (attempts >= maxAttempts) {
      throw new Error(`Reo tracking not available after ${maxAttempts} attempts`);
    }

    // Exponential backoff: 100ms, 200ms, 400ms, 800ms, etc.
    const delay = Math.min(100 * 2 ** (attempts - 1), 5000);
    await new Promise((resolve) => {
      setTimeout(resolve, delay);
    });

    return pollForReo();
  };

  return pollForReo();
};

/**
 * Invokes Reo Sign-in Event with email
 *
 * @param email User's email address
 * @returns Promise<void>
 * @throws Error if Reo tracking fails
 */
export const invokeReoSignInEventWithEmail = async (email: string) => {
  // Invoke Reo Sign-in Event
  const Reo = await ensureReo();
  Reo.identify({
    username: email,
    type: 'email',
  });
};
